import { useEffect, useMemo, useRef } from "react";
import {
  ReactFlow,
  Background,
  Controls,
  MarkerType,
  Handle,
  Position,
  useNodesState,
  useEdgesState
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { GitBranch, Cable, Play } from "lucide-react";
import { cn } from "../../lib/cn";
import { reachableFrom } from "../../lib/graph";
import { loadPositions, savePositions } from "../../lib/workflowLayout";

const NODE_WIDTH = 240;
const NODE_HEIGHT = 84;

/** Short human label for a branch's Jinja `when` (no full parser). */
function describeCondition(jinja) {
  if (!jinja) return "condition";
  const inner = String(jinja).replace(/^\{\{\s*|\s*\}\}$/g, "").trim();
  return inner.length > 30 ? inner.slice(0, 27) + "…" : inner;
}

// Naive layered layout — BFS by depth, for steps without a saved position.
function layout(definition) {
  const positions = {};
  const visited = new Set();
  const layers = [];
  const queue = [{ name: definition.start_step, depth: 0 }];
  while (queue.length > 0) {
    const { name, depth } = queue.shift();
    if (visited.has(name)) continue;
    visited.add(name);
    layers[depth] = layers[depth] ?? [];
    layers[depth].push(name);
    const step = definition.steps[name];
    if (!step) continue;
    if (step.type === "endpoint_call") {
      for (const child of step.next || []) queue.push({ name: child, depth: depth + 1 });
    }
    if (step.type === "branch") {
      for (const b of step.branches || []) queue.push({ name: b.next, depth: depth + 1 });
      if (step.default_next) queue.push({ name: step.default_next, depth: depth + 1 });
    }
  }
  const orphans = Object.keys(definition.steps).filter((s) => !visited.has(s));
  if (orphans.length > 0) layers.push(orphans);

  layers.forEach((layer, layerIdx) => {
    layer.forEach((name, nodeIdx) => {
      positions[name] = {
        x: layerIdx * (NODE_WIDTH + 100),
        y:
          nodeIdx * (NODE_HEIGHT + 40) -
          ((layer.length - 1) * (NODE_HEIGHT + 40)) / 2
      };
    });
  });
  return positions;
}

function statusColors(s) {
  if (!s) return { border: "border-gray-200", dot: "bg-gray-300" };
  if (s === "success" || s === "succeeded") return { border: "border-green-400", dot: "bg-green-500" };
  if (s === "failed" || s === "error") return { border: "border-red-400", dot: "bg-red-500" };
  if (s === "running" || s === "pending") return { border: "border-blue-400", dot: "bg-blue-500" };
  return { border: "border-gray-200", dot: "bg-gray-300" };
}

function methodChipClass(method) {
  const m = (method || "").toUpperCase();
  if (m === "GET") return "bg-blue-100 text-blue-700";
  if (m === "POST") return "bg-emerald-100 text-emerald-700";
  if (m === "PUT" || m === "PATCH") return "bg-amber-100 text-amber-700";
  if (m === "DELETE") return "bg-red-100 text-red-700";
  return "bg-gray-100 text-gray-700";
}

// Compact display for a result value shown as an on-node chip.
function fmtChip(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "boolean") return v ? "Yes" : "No";
  const s = String(v);
  return s.length > 18 ? s.slice(0, 17) + "…" : s;
}

function fmtDur(ms) {
  if (ms == null) return null;
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

// Custom node — card with colored icon tile + connection handles.
function StepNode(props) {
  const data = props.data;
  const { step, ep, status, isStart, selected, dimmed } = data;
  const colors = statusColors(status);
  const isBranch = step.type === "branch";
  const tile = isBranch ? "bg-violet-50 text-violet-600" : "bg-blue-50 text-blue-600";

  return (
    <div
      className={cn(
        "w-full rounded-xl border bg-white shadow-sm hover:shadow-md transition-all p-3 flex gap-2.5",
        status ? colors.border : "border-gray-200",
        selected ? "ring-2 ring-blue-500 ring-offset-2" : "",
        dimmed && !selected ? "opacity-55" : ""
      )}
      style={{ width: NODE_WIDTH }}
      title={dimmed ? "Not connected yet — drag an edge into this step" : undefined}
    >
      <Handle type="target" position={Position.Left} className="!w-2 !h-2 !bg-gray-300 !border-0" />
      <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0", tile)}>
        {isBranch ? <GitBranch size={15} /> : <Cable size={15} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          {isStart && (
            <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-gray-400">
              <Play size={8} fill="currentColor" /> Start
            </span>
          )}
          <span className={cn("inline-block w-1.5 h-1.5 rounded-full", colors.dot, status === "running" && "animate-pulse")} />
        </div>
        <div className="font-mono text-sm font-medium text-gray-900 truncate">{data.name}</div>
        {step.type === "endpoint_call" && ep && (
          <div className="flex items-center gap-1.5 mt-1">
            <span className={cn("inline-flex items-center justify-center rounded font-mono text-[9px] font-bold px-1 py-0.5 min-w-[32px]", methodChipClass(ep.http_method))}>
              {ep.http_method}
            </span>
            <span className="text-[10px] text-gray-500 truncate flex-1">{ep.endpoint_name}</span>
          </div>
        )}
        {step.type === "endpoint_call" && !ep && (
          <div className="text-[10px] text-amber-600 italic mt-1">No endpoint chosen</div>
        )}
        {isBranch && (
          <div className="text-[10px] text-gray-500 mt-0.5">
            {(step.branches || []).length} condition
            {(step.branches || []).length === 1 ? "" : "s"}
          </div>
        )}
        {(data.durationMs != null || (data.chips && data.chips.length > 0)) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {(data.chips || []).slice(0, 2).map((c) => (
              <span
                key={c.key}
                className="inline-flex max-w-[130px] items-center gap-1 rounded bg-green-50 px-1.5 py-0.5 text-[9px]"
                title={`${c.key}: ${fmtChip(c.value)}`}
              >
                <span className="text-green-500 truncate">{c.key}</span>
                <span className="font-semibold text-green-800 truncate">{fmtChip(c.value)}</span>
              </span>
            ))}
            {data.chips && data.chips.length > 2 && (
              <span className="text-[9px] text-gray-400">+{data.chips.length - 2}</span>
            )}
            {data.durationMs != null && (
              <span className="ml-auto text-[9px] text-gray-400">{fmtDur(data.durationMs)}</span>
            )}
          </div>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={!isBranch}
        className={cn("!w-2.5 !h-2.5 !border-2 !border-white", isBranch ? "!bg-gray-300" : "!bg-blue-500")}
      />
    </div>
  );
}

const nodeTypes = { step: StepNode };

function buildNodes(
  definition,
  getPos,
  statusByName,
  selectedStep,
  epById,
  chipsByStep = {},
  durationByStep = {}
) {
  const reachable = reachableFrom(definition.steps, definition.start_step);
  return Object.entries(definition.steps).map(([name, step]) => ({
    id: name,
    type: "step",
    position: getPos(name),
    data: {
      name,
      step,
      ep: step.type === "endpoint_call" ? epById[step.endpoint_id] ?? null : null,
      status: statusByName[name],
      isStart: name === definition.start_step,
      selected: selectedStep === name,
      dimmed: !reachable.has(name),
      chips: chipsByStep[name] || null,
      durationMs: durationByStep[name]
    }
  }));
}

// Map each result field back to the step that produced it, by scanning the
// outputs templates for `steps.<name>`. Lets nodes show the data they yielded.
function deriveChipsByStep(definition, result) {
  if (!result || typeof result !== "object") return {};
  const outputs = definition.outputs || {};
  const map = {};
  for (const [key, tmpl] of Object.entries(outputs)) {
    const m = String(tmpl).match(/steps\.([A-Za-z0-9_]+)/);
    if (!m) continue;
    const step = m[1];
    if (result[key] === undefined) continue;
    (map[step] = map[step] || []).push({ key, value: result[key] });
  }
  return map;
}

const DONE = new Set(["success", "succeeded", "completed"]);

// An edge "flows" (marching ants + blue highlight) when its target step is
// currently running and its source has finished — the live data hand-off.
// Edges on the already-completed path get a solid green tint.
function runEdgeStyle(statusByName, source, target, base) {
  const src = (statusByName[source] || "").toLowerCase();
  const tgt = (statusByName[target] || "").toLowerCase();
  if (tgt === "running" || tgt === "pending") {
    return {
      animated: true,
      style: { ...base, stroke: "#2563eb", strokeWidth: (base.strokeWidth || 1.5) + 0.75 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#2563eb" }
    };
  }
  if (DONE.has(src) && DONE.has(tgt)) {
    return {
      animated: false,
      style: { ...base, stroke: "#16a34a", strokeWidth: (base.strokeWidth || 1.5) + 0.25 },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#16a34a" }
    };
  }
  return { animated: false, style: base, markerEnd: null };
}

function buildEdges(definition, statusByName = {}) {
  const es = [];
  const hasRun = Object.keys(statusByName).length > 0;
  for (const [name, step] of Object.entries(definition.steps)) {
    if (step.type === "endpoint_call") {
      const fanOut = (step.next || []).length > 1;
      (step.next || []).forEach((child, idx) => {
        const base = { stroke: fanOut ? "#7c3aed" : "#475569", strokeWidth: fanOut ? 2 : 1.5 };
        const run = hasRun ? runEdgeStyle(statusByName, name, child, base) : null;
        es.push({
          id: `${name}->${child}@${idx}`,
          source: name,
          target: child,
          deletable: true,
          animated: run?.animated || false,
          label: fanOut ? `parallel · ${idx + 1} of ${step.next.length}` : undefined,
          labelStyle: fanOut ? { fontSize: 10, fontFamily: "monospace", fill: "#7c3aed" } : undefined,
          labelBgStyle: fanOut ? { fill: "#f5f3ff" } : undefined,
          labelBgPadding: fanOut ? [4, 2] : undefined,
          labelBgBorderRadius: fanOut ? 3 : undefined,
          markerEnd: run?.markerEnd || { type: MarkerType.ArrowClosed, color: fanOut ? "#7c3aed" : "#475569" },
          style: run?.style || base
        });
      });
    }
    if (step.type === "branch") {
      (step.branches || []).forEach((b, idx) => {
        const desc = describeCondition(b.when) || `Condition ${idx + 1}`;
        const base = { stroke: "#3b82f6", strokeWidth: 1.5 };
        const run = hasRun ? runEdgeStyle(statusByName, name, b.next, base) : null;
        es.push({
          id: `${name}->${b.next}@${idx}`,
          source: name,
          target: b.next,
          deletable: false,
          animated: run?.animated || false,
          label: desc,
          labelStyle: { fontSize: 11, fontFamily: "monospace", fill: "#1e40af" },
          labelBgStyle: { fill: "#dbeafe" },
          labelBgPadding: [6, 4],
          labelBgBorderRadius: 4,
          markerEnd: run?.markerEnd || { type: MarkerType.ArrowClosed, color: "#3b82f6" },
          style: run?.style || base
        });
      });
      if (step.default_next) {
        const base = { strokeDasharray: "4 4", stroke: "#94a3b8", strokeWidth: 1.5 };
        const run = hasRun ? runEdgeStyle(statusByName, name, step.default_next, base) : null;
        es.push({
          id: `${name}->${step.default_next}@default`,
          source: name,
          target: step.default_next,
          deletable: false,
          animated: run?.animated || false,
          label: "otherwise",
          labelStyle: { fontSize: 11, fontFamily: "monospace", fill: "#64748b" },
          labelBgStyle: { fill: "#f1f5f9" },
          labelBgPadding: [6, 4],
          labelBgBorderRadius: 4,
          markerEnd: run?.markerEnd || { type: MarkerType.ArrowClosed, color: "#94a3b8" },
          style: run?.style || base
        });
      }
    }
  }
  return es;
}

// Changes only when graph shape/labels change, NOT when positions move.
function structuralSig(definition) {
  const steps = Object.entries(definition.steps).map(([k, s]) =>
    s.type === "endpoint_call"
      ? [k, "e", s.endpoint_id, s.next]
      : [k, "b", (s.branches || []).map((b) => [b.when, b.next]), s.default_next]
  );
  return JSON.stringify({ start: definition.start_step, steps });
}

export default function GraphView({
  definition,
  endpoints,
  run,
  selectedStep,
  onSelectStep,
  workflowId,
  onConnectStep,
  onDeleteStep,
  onDeleteEdge,
  interactive = false
}) {
  const stepStatusByName = useMemo(() => {
    const m = {};
    if (run) for (const s of run.steps || []) m[s.step_name] = s.status;
    return m;
  }, [run]);

  const durationByName = useMemo(() => {
    const m = {};
    if (run)
      for (const s of run.steps || [])
        if (s.duration_ms != null) m[s.step_name] = s.duration_ms;
    return m;
  }, [run]);

  const chipsByStep = useMemo(
    () => deriveChipsByStep(definition, run?.result),
    [definition, run]
  );

  const epById = useMemo(() => {
    const m = {};
    for (const e of endpoints || []) m[e.endpoint_id] = e;
    return m;
  }, [endpoints]);

  const posRef = useRef(loadPositions(workflowId));
  const signature = useMemo(() => structuralSig(definition), [definition]);
  const statusSig = useMemo(() => JSON.stringify(stepStatusByName), [stepStatusByName]);
  const metaSig = useMemo(
    () => JSON.stringify({ d: durationByName, c: chipsByStep }),
    [durationByName, chipsByStep]
  );
  const epSig = useMemo(() => Object.keys(epById).length, [epById]);

  const initialNodes = useMemo(() => {
    const computed = layout(definition);
    return buildNodes(
      definition,
      (n) => posRef.current[n] ?? computed[n] ?? { x: 0, y: 0 },
      stepStatusByName,
      selectedStep,
      epById,
      chipsByStep,
      durationByName
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    buildEdges(definition, stepStatusByName)
  );

  useEffect(() => {
    const computed = layout(definition);
    setNodes((cur) => {
      const live = {};
      for (const n of cur) live[n.id] = n.position;
      return buildNodes(
        definition,
        (name) => live[name] ?? posRef.current[name] ?? computed[name] ?? { x: 0, y: 0 },
        stepStatusByName,
        selectedStep,
        epById,
        chipsByStep,
        durationByName
      );
    });
    setEdges(buildEdges(definition, stepStatusByName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, statusSig, metaSig, selectedStep, epSig]);

  const onConnect = (c) => {
    if (c.source && c.target) onConnectStep?.(c.source, c.target);
  };

  const onNodeDragStop = (_, node) => {
    posRef.current = { ...posRef.current, [node.id]: node.position };
    savePositions(workflowId, posRef.current);
  };

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onNodeClick={(_, node) => onSelectStep?.(node.id)}
      onNodeDragStop={onNodeDragStop}
      onNodesDelete={(deleted) => deleted.forEach((n) => onDeleteStep?.(n.id))}
      onEdgesDelete={(deleted) => deleted.forEach((e) => onDeleteEdge?.(e.id))}
      nodesDraggable
      nodesConnectable={interactive}
      elementsSelectable
      fitView
      fitViewOptions={{ padding: 0.2 }}
      proOptions={{ hideAttribution: true }}
    >
      <Background gap={20} size={1} color="#e2e8f0" />
      <Controls showInteractive={false} />
    </ReactFlow>
  );
}
